import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './GastosRodada.css';

/**
 * Página para Admin gerenciar gastos por rodada
 */

const GastosRodada = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rodadas, setRodadas] = useState([]);
  const [rodadaSelecionada, setRodadaSelecionada] = useState('');
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoGasto, setEditandoGasto] = useState(null);
  const [formData, setFormData] = useState({ descricao: '', valor: '' });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarRodadas();
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (rodadaSelecionada) {
      carregarGastos();
    }
  }, [rodadaSelecionada]);

  const carregarRodadas = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get('/rodadas');
      const rodadasOrdenadas = response.data.sort((a, b) => 
        new Date(b.data) - new Date(a.data)
      );
      setRodadas(rodadasOrdenadas);
      
      if (rodadasOrdenadas.length > 0 && !rodadaSelecionada) {
        setRodadaSelecionada(rodadasOrdenadas[0].id);
      }
    } catch (err) {
      console.error('Erro ao carregar rodadas:', err);
      setError(err.response?.data?.error || 'Erro ao carregar rodadas');
    } finally {
      setLoading(false);
    }
  };

  const carregarGastos = async () => {
    if (!rodadaSelecionada) return;

    try {
      setLoading(true);
      const response = await apiClient.get(`/gastos/rodada/${rodadaSelecionada}`);
      setGastos(response.data || []);
      setError('');
    } catch (err) {
      console.error('Erro ao carregar gastos:', err);
      setError(err.response?.data?.error || 'Erro ao carregar gastos');
    } finally {
      setLoading(false);
    }
  };

  const handleNovoGasto = () => {
    setEditandoGasto(null);
    setFormData({ descricao: '', valor: '' });
    setMostrarForm(true);
    setError('');
    setSuccess('');
  };

  const handleEditarGasto = (gasto) => {
    setEditandoGasto(gasto);
    setFormData({
      descricao: gasto.descricao,
      valor: parseFloat(gasto.valor).toFixed(2),
    });
    setMostrarForm(true);
    setError('');
    setSuccess('');
  };

  const handleCancelarForm = () => {
    setMostrarForm(false);
    setEditandoGasto(null);
    setFormData({ descricao: '', valor: '' });
    setError('');
  };

  const handleSalvarGasto = async (e) => {
    e.preventDefault();

    if (!formData.descricao.trim()) {
      setError('Descrição é obrigatória');
      return;
    }

    const valor = parseFloat(formData.valor);
    if (isNaN(valor) || valor <= 0) {
      setError('Valor deve ser um número positivo');
      return;
    }

    try {
      setSalvando(true);
      setError('');
      setSuccess('');

      if (editandoGasto) {
        await apiClient.put(`/gastos/${editandoGasto.id}`, {
          descricao: formData.descricao.trim(),
          valor: valor,
        });
        setSuccess('Gasto atualizado com sucesso!');
      } else {
        await apiClient.post('/gastos', {
          rodada_id: rodadaSelecionada,
          descricao: formData.descricao.trim(),
          valor: valor,
        });
        setSuccess('Gasto adicionado com sucesso!');
      }

      setMostrarForm(false);
      setEditandoGasto(null);
      setFormData({ descricao: '', valor: '' });
      await carregarGastos();
    } catch (err) {
      console.error('Erro ao salvar gasto:', err);
      setError(err.response?.data?.error || 'Erro ao salvar gasto');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluirGasto = async (gastoId) => {
    if (!window.confirm('Deseja realmente excluir este gasto?')) {
      return;
    }

    try {
      setSalvando(true);
      setError('');
      await apiClient.delete(`/gastos/${gastoId}`);
      setSuccess('Gasto excluído com sucesso!');
      await carregarGastos();
    } catch (err) {
      console.error('Erro ao excluir gasto:', err);
      setError(err.response?.data?.error || 'Erro ao excluir gasto');
    } finally {
      setSalvando(false);
    }
  };

  const rodadaAtual = rodadas.find(r => r.id === rodadaSelecionada);
  const totalGastos = gastos.reduce((sum, g) => sum + parseFloat(g.valor || 0), 0);

  if (loading && rodadas.length === 0) {
    return <div className="container">Carregando...</div>;
  }

  return (
    <div className="container gastos-rodada-container">
      <div className="page-header">
        <h1>💸 Gastos NerdzPoker por Rodada</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {/* Seletor de Rodada */}
      <div className="rodada-selector">
        <label>Selecione a Rodada:</label>
        <select
          value={rodadaSelecionada}
          onChange={(e) => setRodadaSelecionada(e.target.value)}
          className="select-rodada"
        >
          {rodadas.map(r => (
            <option key={r.id} value={r.id}>
              {new Date(r.data).toLocaleDateString('pt-BR')} - {r.status === 'fechada' ? 'Fechada' : 'Aberta'}
            </option>
          ))}
        </select>
      </div>

      {rodadaAtual && (
        <div className="rodada-info">
          <p><strong>Rodada:</strong> {new Date(rodadaAtual.data).toLocaleDateString('pt-BR')}</p>
          <p><strong>Status:</strong> {rodadaAtual.status === 'fechada' ? 'Fechada' : 'Aberta'}</p>
        </div>
      )}

      {/* Total de Gastos */}
      <div className="total-gastos-card">
        <h3>Total de Gastos desta Rodada</h3>
        <p className="valor-total">R$ {totalGastos.toFixed(2)}</p>
      </div>

      {/* Botão Novo Gasto */}
      {!mostrarForm && (
        <div className="actions-header">
          <button onClick={handleNovoGasto} className="btn btn-primary">
            ➕ Adicionar Gasto
          </button>
        </div>
      )}

      {/* Formulário de Gasto */}
      {mostrarForm && (
        <div className="gasto-form-card">
          <h3>{editandoGasto ? '✏️ Editar Gasto' : '➕ Novo Gasto'}</h3>
          <form onSubmit={handleSalvarGasto}>
            <div className="form-group">
              <label>Descrição *</label>
              <input
                type="text"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                className="form-input"
                placeholder="Ex: Compras de bebidas, Material de jogo, etc."
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Valor (R$) *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.valor}
                onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                className="form-input"
                placeholder="0.00"
                required
              />
            </div>
            <div className="form-actions">
              <button type="submit" disabled={salvando} className="btn btn-primary">
                {salvando ? 'Salvando...' : editandoGasto ? 'Atualizar' : 'Adicionar'}
              </button>
              <button 
                type="button" 
                onClick={handleCancelarForm} 
                disabled={salvando}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Gastos */}
      <div className="gastos-list">
        <h3>Gastos Registrados</h3>
        {gastos.length === 0 ? (
          <div className="no-gastos">
            <p>Nenhum gasto registrado para esta rodada.</p>
          </div>
        ) : (
          <div className="gastos-table-container">
            <table className="gastos-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Descrição</th>
                  <th>Valor</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {gastos.map(gasto => (
                  <tr key={gasto.id}>
                    <td>{new Date(gasto.createdAt).toLocaleDateString('pt-BR')}</td>
                    <td>{gasto.descricao}</td>
                    <td className="valor-gasto">R$ {parseFloat(gasto.valor).toFixed(2)}</td>
                    <td>
                      <div className="actions-buttons">
                        <button
                          onClick={() => handleEditarGasto(gasto)}
                          className="btn btn-sm btn-secondary"
                          disabled={salvando}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => handleExcluirGasto(gasto.id)}
                          className="btn btn-sm btn-danger"
                          disabled={salvando}
                        >
                          🗑️ Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GastosRodada;
